import {clone, cloneDeep, get, isArray, isEmpty, isNumber, isObject, isString, setWith, toPath, unset} from 'lodash';

export const getIn = (state, path, defaultValue) => {
  return isEmpty(path) ? state : get(state, path, defaultValue);
};

export const setIn = (state, path, valueToSet) => {
  if (isEmpty(path)) {
    return isObject(valueToSet) ? {...state, ...valueToSet} : valueToSet;
  }
  return setWith({...state}, path, valueToSet, (nsValue, key) => {
    const nextKey = path[path.lastIndexOf(key) + 1];
    const isStringNumber = isString(nextKey) && isNumber(parseInt((nextKey), 10));
    const result = isStringNumber ? Object(nsValue) : nsValue;
    return clone(result);
  });
};

/**
 * Creates new object with property on path deleted from it.
 * If clean empty parent objects flag is set, it will delete parent object in case they don't have any other property (are empty).
 * THis cleanup is performed until an object is found with other properties in the hierarchy.
 *
 * @param state - object to delete in
 * @param path - path to delete
 * @param cleanEmptyParentObjects - flag to clean empty parent objects
 * @returns {{}|*}
 */
export const deleteIn = (state, path, cleanEmptyParentObjects = false) => {
  const newState = cloneDeep(state);

  if (isEmpty(path)) {
    return newState;
  }

  const paths = toPath(path);

  if (cleanEmptyParentObjects) {
    const length = paths.length;
    let index,
      object,
      deletePath;

    index = 0;
    deletePath = null;
    object = newState;

    while (object != null && index < length) {
      if (deletePath === null) {
        if (Object.keys(object).length < 2 && object[paths[index]] !== undefined) {
          // get path to first object key which doesn't have any siblings
          deletePath = paths.slice(0, index);
        }
      } else {
        if (Object.keys(object).length > 1 || object[paths[index]] === undefined) {
          // reset first object key if there is one which have siblings
          deletePath = null;
        }
      }
      object = object[paths[index++]];
    }

    deletePath = deletePath ?? paths;
    const parent = get(newState, deletePath.slice(0, deletePath.length - 1)) ?? newState;

    if (deletePath.length === 0) {
      return isArray(newState) ? [] : {}; // when we need to delete whole object, since at the end it only contains one property
    } else if (isArray(parent)) {
      parent.splice(+deletePath[deletePath.length - 1], 1);
    } else {
      unset(newState, deletePath);
    }
  } else {
    unset(newState, paths);
  }

  return newState;
};

export const getParentPath = (path) => {
  if (isEmpty(path)) {
    return null;
  }
  return path.substr(0, path.lastIndexOf(".")) || null;
};

export function isAnyPropertyNonEmpty(obj) {
  return Object.values(obj).some(value => !isEmpty(value));
}